import * as React from 'react'
import { theme } from '../theme'
import { ActionButtons } from '../editor/ActionButtons'
import { ImageTransform, EmptySpaceCalculation } from './types'
import { AspectOption } from '../dimension_management/DimensionOptions'
import { TransformControls } from './TransformControls'

interface Props {
	selectedDimension: AspectOption
	transform: ImageTransform
	emptySpace: EmptySpaceCalculation
	imageWidth: number
	imageHeight: number
	onReturn: () => void
	onAccept: () => void
	getScaledDimensions: (width: number, height: number) => { width: number; height: number }
	setTransform?: (transform: ImageTransform) => void
}

export const DimensionsPanel = ({
	transform,
	emptySpace,
	onReturn,
	onAccept,
	setTransform,
}: Props) => {
	// Handle transform changes from the controls
	const handleTransformChange = React.useCallback(
		(newTransform: ImageTransform) => {
			setTransform?.(newTransform)
		},
		[setTransform],
	)

	// Determine the accept button text based on emptySpace calculations
	const getAcceptButtonText = React.useCallback(() => {
		// Check if there are any positive values (areas that need to be filled)
		const hasPositiveValues =
			emptySpace.originalEmptyUp > 0 ||
			emptySpace.originalEmptyRight > 0 ||
			emptySpace.originalEmptyDown > 0 ||
			emptySpace.originalEmptyLeft > 0

		// Check if there are any negative values (areas that need to be cropped)
		const hasNegativeValues =
			emptySpace.originalEmptyUp < 0 ||
			emptySpace.originalEmptyRight < 0 ||
			emptySpace.originalEmptyDown < 0 ||
			emptySpace.originalEmptyLeft < 0

		// Determine the button text based on the combination of values
		if (hasPositiveValues && hasNegativeValues) {
			return 'Generate Fill and Crop'
		} else if (hasPositiveValues) {
			return 'Generate Fill'
		} else if (hasNegativeValues) {
			return 'Crop'
		} else {
			// Default case (no changes needed)
			return 'Accept'
		}
	}, [emptySpace])

	return (
		<div
			style={{
				padding: theme.spacing.md,
				backgroundColor: theme.colors.surface,
				borderRadius: theme.borderRadius.sm,
				border: `1px solid ${theme.colors.border}`,
				maxWidth: '100%',
				overflow: 'auto',
			}}
		>
			{/* Title with divider */}
			<div style={{ marginBottom: theme.spacing.md }}>
				<h3
					style={{
						margin: 0,
						marginBottom: theme.spacing.sm,
						fontSize: theme.typography.sizes.sm,
						color: theme.colors.text.primary,
						fontWeight: 500,
					}}
				>
					Adjust Image Position & Scale
				</h3>
				<div
					style={{
						height: '2px',
						background: `linear-gradient(to right, ${theme.colors.primary}40, ${theme.colors.primary}10)`,
						borderRadius: '2px',
						marginBottom: theme.spacing.md,
					}}
				/>

				{/* Explanatory text */}
				<p
					style={{
						margin: 0,
						fontSize: theme.typography.sizes.tiny,
						color: theme.colors.text.secondary,
						lineHeight: '1.5',
					}}
				>
					Align your image by <strong>dragging</strong> it with your mouse or <strong>scrolling</strong> to
					zoom in/out. You can also use the controls below to make precise adjustments.
				</p>
			</div>

			{/* Transform controls */}
			{setTransform && (
				<div style={{ marginBottom: theme.spacing.md }}>
					<TransformControls transform={transform} onTransformChange={handleTransformChange} />
				</div>
			)}

			{/* Action buttons with divider */}
			<div
				style={{
					marginTop: theme.spacing.md,
					borderTop: `1px solid ${theme.colors.border}`,
					paddingTop: theme.spacing.md,
				}}
			>
				<ActionButtons
					isReturnDisabled={transform.scale === 1 && transform.position.x === 0 && transform.position.y === 0}
					isAcceptDisabled={
						emptySpace.originalEmptyUp === 0 &&
						emptySpace.originalEmptyRight === 0 &&
						emptySpace.originalEmptyDown === 0 &&
						emptySpace.originalEmptyLeft === 0
					}
					onReturn={onReturn}
					onAccept={onAccept}
					variant="compact"
					acceptLabel={getAcceptButtonText()}
				/>
			</div>
		</div>
	)
}
